import { useState, FormEvent } from "react";
import { locations, contacts } from "@/mocks/site-data";

export default function Contato() {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = (formData.get("company_alt") as string || "").trim();
    if (honeypot.length > 0) {
      setFormStatus("success");
      return;
    }

    formData.delete("company_alt");

    const nome = (formData.get("nome") as string || "").trim();
    const email = (formData.get("email") as string || "").trim();
    const mensagem = (formData.get("mensagem") as string || "").trim();

    if (!nome || !email || !mensagem) {
      setFormStatus("error");
      setFormError("Por favor, preencha todos os campos obrigatórios.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setFormStatus("error");
      setFormError("Por favor, insira um e-mail válido.");
      return;
    }

    setFormStatus("submitting");
    setFormError("");

    try {
      const response = await fetch("https://readdy.ai/api/form/d9r6m36e3oq4jqumtu3g", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam") || responseText.toLowerCase().includes("spam");
      const isSuccess = response.ok && parsed?.code === "OK" && !isSpam;

      if (isSuccess) {
        setFormStatus("success");
        formEl.reset();
      } else {
        setFormStatus("error");
        setFormError(serverMsg || "Erro ao enviar mensagem. Tente novamente.");
      }
    } catch {
      setFormStatus("error");
      setFormError("Erro de conexão. Verifique sua internet e tente novamente.");
    }
  };

  return (
    <main className="pt-20 md:pt-24">
      <section className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="text-center mb-14 md:mb-20 max-w-2xl mx-auto">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold tracking-wide uppercase mb-4">
              Fale Conosco
            </span>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-900 font-heading mb-4">
              Entre em Contato
            </h1>
            <div className="w-16 h-1 bg-accent-500 mx-auto rounded-full mb-5" />
            <p className="text-foreground-600 text-base md:text-lg">
              Estamos prontos para atender sua instituição. Escolha o canal que preferir.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-10 max-w-6xl mx-auto">
            <div className="lg:col-span-3">
              <div className="bg-background-50 border border-background-200 rounded-xl p-6 md:p-8">
                <h2 className="text-lg font-bold text-foreground-900 font-heading mb-6">
                  Envie uma mensagem
                </h2>
                <form id="contato-redenet" data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="company-alt-field"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="nome" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Nome
                      </label>
                      <input
                        type="text"
                        id="nome"
                        name="nome"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                        placeholder="Seu nome"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        E-mail
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                        placeholder="seu@email.com"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="telefone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Telefone
                      </label>
                      <input
                        type="tel"
                        id="telefone"
                        name="telefone"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                        placeholder="(00) 00000-0000"
                      />
                    </div>
                    <div>
                      <label htmlFor="instituicao" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Instituição
                      </label>
                      <input
                        type="text"
                        id="instituicao"
                        name="instituicao"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                        placeholder="Nome da escola/instituição"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="interesse" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Produto de Interesse
                    </label>
                    <select
                      id="interesse"
                      name="interesse"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                    >
                      <option value="">Selecione um produto</option>
                      <option value="ie">IE - Gestão Educacional</option>
                      <option value="rface">RFACE - Reconhecimento Facial</option>
                      <option value="iluminia">iLuminIA - Educação Especial</option>
                      <option value="saecta">SAECTA - Avaliações</option>
                      <option value="outro">Outro / Geral</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="mensagem" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Mensagem
                    </label>
                    <textarea
                      id="mensagem"
                      name="mensagem"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 resize-none"
                      placeholder="Descreva sua necessidade..."
                    />
                    <p className="text-xs text-foreground-500 mt-1">Máximo 500 caracteres</p>
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === "submitting"}
                    className="w-full sm:w-auto px-8 py-3 rounded-full bg-primary-600 text-background-50 text-sm font-semibold hover:bg-primary-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {formStatus === "submitting" ? "Enviando..." : "Enviar Mensagem"}
                  </button>
                  {formStatus === "success" && (
                    <div className="p-3 rounded-lg bg-accent-50 border border-accent-200 text-accent-800 text-sm">
                      Mensagem enviada com sucesso! Entraremos em contato em breve.
                    </div>
                  )}
                  {formStatus === "error" && (
                    <div className="p-3 rounded-lg bg-background-100 border border-background-200 text-foreground-700 text-sm">
                      {formError}
                    </div>
                  )}
                </form>
              </div>
            </div>

            <div className="lg:col-span-2 flex flex-col gap-6">
              <div className="bg-background-50 border border-background-200 rounded-xl p-6">
                <h3 className="text-sm font-semibold text-foreground-900 uppercase tracking-wide mb-5">
                  WhatsApp
                </h3>
                <div className="flex flex-col gap-3">
                  {contacts.phones
                    .filter((p) => p.type === "whatsapp")
                    .map((phone) => (
                      <a
                        key={phone.value}
                        href={`https://wa.me/55${phone.value.replace(/\D/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 rounded-lg bg-accent-50 border border-accent-100 hover:bg-accent-100 transition-colors"
                      >
                        <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center shrink-0">
                          <i className="ri-whatsapp-line text-background-50" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground-900">{phone.label}</p>
                          <p className="text-sm text-foreground-600">{phone.value}</p>
                        </div>
                      </a>
                    ))}
                </div>
              </div>

              <div className="bg-background-50 border border-background-200 rounded-xl p-6">
                <h3 className="text-sm font-semibold text-foreground-900 uppercase tracking-wide mb-5">
                  E-mails
                </h3>
                <div className="flex flex-col gap-2.5">
                  {contacts.emails.map((email) => (
                    <a
                      key={email.value}
                      href={`mailto:${email.value}`}
                      className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                    >
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-mail-line text-primary-500" />
                      </div>
                      <span>{email.value}</span>
                    </a>
                  ))}
                </div>
              </div>

              <div className="bg-background-50 border border-background-200 rounded-xl p-6">
                <h3 className="text-sm font-semibold text-foreground-900 uppercase tracking-wide mb-5">
                  Endereços
                </h3>
                <div className="flex flex-col gap-4">
                  {locations.map((loc) => (
                    <div key={loc.id} className="text-sm">
                      <p className="font-semibold text-foreground-900 text-sm">
                        {loc.city} - {loc.state}
                      </p>
                      <p className="text-foreground-600 mt-0.5">{loc.addressLine1}</p>
                      {loc.addressLine2 && <p className="text-foreground-600">{loc.addressLine2}</p>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}